import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { getJobReferralCode } from '@/services/api/homeApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { showToast } from '@/utils/globalToast';
import { JOB_SHARE_REF } from '@/features/jobs/utils/jobOrigin';
import { copyToClipboard } from '@/features/jobs/utils/jobHelpers';
import { LinkIcon } from './SocialIcons';
import type { LinkOption } from '@/types/tenant.types';
import styles from './ShareJobMenu.module.css';

/**
 * ShareJobMenu — mirrors Flutter ShareUrlMenuPopupWidget + JobShareBloc.
 *
 * - Options come from tenant contentSettings.shareJobPlatform (enabled only, in list order);
 *   with none configured the menu is not rendered.
 * - Icons: Copy Link uses the bundled link icon (Flutter copyLinkIconPng, tinted
 *   ColorsConstants.linkColor); every other option shows its tenant `iconUrl`, tinted only for
 *   X / Facebook / LinkedIn (_getIconColor → NetworkSvgImageWidget colorFilter).
 * - Preference `jobShareEnabledWithReferralCode` → fetch a referral code first and share
 *   `?tenantId=&channel=&sourceName=&title=` links.
 * - Every link carries a `ref` param identifying the channel (read back by captureJobLinkOrigin).
 */

const FACEBOOK_SHARE_URL = 'https://www.facebook.com/sharer/sharer.php?u=';
const LINKEDIN_SHARE_URL_WITH_TITLE = 'https://www.linkedin.com/shareArticle?mini=true&url=';
const TWITTER_SHARE_URL = 'https://x.com/intent/tweet?text=';
const COPY_LINK = 'Copy Link';
/**
 * Flutter AppConstants.linkCopiedToClipboard. Flutter shows it as a 1 s info toast, easy to miss;
 * it is a 3 s success toast here, shown once the copy has actually happened.
 */
const LINK_COPIED = 'Link copied to clipboard !';
/** The copy failed (clipboard blocked or unavailable, or the referral link couldn't be built) */
const LINK_COPY_FAILED = 'Unable to copy the link. Please try again.';
/** Flutter ColorsConstants.linkColor (Colors.blue) */
const COPY_LINK_COLOR = '#2196F3';

/** Flutter _ShareUrlMenuPopupWidgetState._getIconColor — srcIn tint for these platforms only */
function getIconTint(title: string): string | undefined {
  if (title === 'X') return '#000000';
  if (title === 'Facebook') return '#1877F2';
  if (title === 'LinkedIn') return '#0077B5';
  return undefined;
}

interface ShareJobMenuProps {
  /** `${origin}/jobs/${displayId}` */
  shareUrl: string;
  jobId?: string;
  jobTitle?: string;
  isDisabled?: boolean;
  className?: string;
  /** Restyles the share button (e.g. on the job page's coloured hero). */
  triggerClassName?: string;
}

function openUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Flutter JobShareBloc._GetJobReferralCode title encoding */
function encodeTitle(jobTitle: string): string {
  if (jobTitle.includes(' ')) return jobTitle.replace(/ /g, '_');
  if (jobTitle.includes('_')) return jobTitle.replace(/_/g, '__');
  return jobTitle;
}

export function ShareJobMenu({
  shareUrl,
  jobId = '',
  jobTitle = '',
  isDisabled = false,
  className,
  triggerClassName,
}: ShareJobMenuProps) {
  const { t } = useTranslation();
  const content = useTenantStore((s) => s.content);
  const preferences = useTenantStore((s) => s.preferences);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = useMemo(
    () => (content?.contentSettings?.shareJobPlatform ?? []).filter((o) => o.enable),
    [content],
  );
  const withReferralCode =
    (preferences as Record<string, unknown> | null)?.jobShareEnabledWithReferralCode === true;

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  if (options.length === 0) return null;

  /** `url` may still be on its way (referral code); the copy starts during the click either way. */
  const copyLink = async (url: string | Promise<string>) => {
    if (await copyToClipboard(url)) {
      void analyticsService.logEvent('Share_Job', 'copy_job_link');
      showToast(LINK_COPIED, 'success');
    } else {
      showToast(LINK_COPY_FAILED, 'error');
    }
  };

  /** Mirrors JobShareBloc._GetJobReferralCode: the share link with the referral parameters */
  const referralShareUrl = async (option: LinkOption): Promise<string> => {
    const data = await getJobReferralCode(jobId, option.title ?? '');
    const workspaceId = localStorage.getItem('workspaceId') ?? '';
    return `${shareUrl}?tenantId=${workspaceId}&channel=${data.channel}&sourceName=${data.userEmailId}&title=${encodeTitle(jobTitle)}`;
  };

  // Mirrors JobShareBloc.shareJob — `sharedUrl` already has a query string
  const shareWithQuery = (option: LinkOption, sharedUrl: string) => {
    const title = option.title ?? '';
    if (title === 'X') {
      openUrl(TWITTER_SHARE_URL + encodeURIComponent(`${sharedUrl}&ref=${JOB_SHARE_REF.X}`));
    } else if (title === 'Facebook') {
      openUrl(FACEBOOK_SHARE_URL + encodeURIComponent(`${sharedUrl}&ref=${JOB_SHARE_REF.FB}`));
    } else if (title === 'LinkedIn') {
      openUrl(
        LINKEDIN_SHARE_URL_WITH_TITLE +
          encodeURIComponent(`${sharedUrl}&decs=long&ref=${JOB_SHARE_REF.LINKEDIN}`),
      );
    } else {
      // (Copy Link is handled by shareWithReferralCode, so the copy starts within the click)
      openUrl(
        (option.linkUrl ?? '') +
          encodeURIComponent(`${sharedUrl}&ref=job-share-${title.toLowerCase()}-link`),
      );
    }
  };

  const shareWithReferralCode = async (option: LinkOption) => {
    if (option.title === COPY_LINK) {
      // Started now, inside the click: the clipboard write waits for the referral link
      void copyLink(referralShareUrl(option).then((url) => `${url}&ref=${JOB_SHARE_REF.DIRECT}`));
      return;
    }
    try {
      shareWithQuery(option, await referralShareUrl(option));
    } catch {
      // Flutter ignores referral-code failures
    }
  };

  const onSelect = (option: LinkOption) => {
    setIsOpen(false);
    const title = option.title ?? '';
    if (withReferralCode) {
      void analyticsService.logJobShare({ jobId, jobTitle, shareMethod: title });
      void shareWithReferralCode(option);
      return;
    }
    if (title === COPY_LINK) {
      const workspaceId = localStorage.getItem('workspaceId') ?? '';
      void copyLink(`${shareUrl}?tenantId=${workspaceId}&ref=${JOB_SHARE_REF.DIRECT}`);
      return;
    }
    void analyticsService.logJobShare({ jobId, jobTitle, shareMethod: title });
    if (title === 'X') {
      openUrl(TWITTER_SHARE_URL + encodeURIComponent(`${shareUrl}?ref=${JOB_SHARE_REF.X}`));
    } else if (title === 'Facebook') {
      openUrl(FACEBOOK_SHARE_URL + encodeURIComponent(`${shareUrl}?ref=${JOB_SHARE_REF.FB}`));
    } else if (title === 'LinkedIn') {
      openUrl(
        LINKEDIN_SHARE_URL_WITH_TITLE + encodeURIComponent(`${shareUrl}?ref=${JOB_SHARE_REF.LINKEDIN}`),
      );
    } else {
      openUrl(
        (option.linkUrl ?? '') +
          encodeURIComponent(`${shareUrl}?ref=job-share-${title.toLowerCase()}-link`),
      );
    }
  };

  return (
    <div
      ref={ref}
      className={`${styles.wrapper} ${className ?? ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ''} ${triggerClassName ?? ''}`}
        onClick={() => setIsOpen((v) => !v)}
        disabled={isDisabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('Share')}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 7a3 3 0 100-6 3 3 0 000 6zM5 13a3 3 0 100-6 3 3 0 000 6zM15 19a3 3 0 100-6 3 3 0 000 6z" />
          <line x1="7.59" y1="11.41" x2="12.42" y2="15.59" />
          <line x1="12.41" y1="4.41" x2="7.59" y2="8.59" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.popup} role="menu">
          {options.map((option, index) => {
            const title = option.title ?? '';
            const tint = title === COPY_LINK ? COPY_LINK_COLOR : getIconTint(title);
            return (
              <button
                key={`${title}-${index}`}
                type="button"
                role="menuitem"
                className={styles.option}
                onClick={() => onSelect(option)}
                title={title}
                aria-label={title}
                style={tint ? ({ '--social-color': tint } as CSSProperties) : undefined}
              >
                {title === COPY_LINK ? (
                  <span className={styles.copyLinkIcon}>
                    <LinkIcon />
                  </span>
                ) : option.iconUrl ? (
                  tint ? (
                    <span
                      className={styles.tintedIcon}
                      style={
                        {
                          WebkitMaskImage: `url("${option.iconUrl}")`,
                          maskImage: `url("${option.iconUrl}")`,
                        } as CSSProperties
                      }
                      aria-hidden="true"
                    />
                  ) : (
                    <img src={option.iconUrl} alt="" className={styles.icon} />
                  )
                ) : (
                  <span className={styles.label}>{title}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
